import React from "react";

const formatDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString("it-IT");
};

const scoreBadge = (score) => {
  if (score == null) return "bg-gray-100 text-gray-500";
  if (score >= 80) return "bg-green-100 text-green-700";
  if (score >= 50) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

const Stat = ({ label, value, accent }) => (
  <div className="flex flex-col">
    <span className="text-[10px] uppercase tracking-wide text-gray-400">
      {label}
    </span>
    <span className={`text-sm font-semibold ${accent || "text-gray-700"}`}>
      {value}
    </span>
  </div>
);

const Note = ({ children }) => (
  <p className="text-xs text-gray-500 italic">{children}</p>
);

function InterventionTimeliness({ dati }) {
  if (!dati || dati.interventiTotali === 0) {
    return <Note>{dati?.nota || "Nessun intervento nel periodo"}</Note>;
  }
  const { interventiConScadenza, interventiInTempo, percentuale } = dati;
  return (
    <div className="flex gap-4 flex-wrap">
      <Stat
        label="In tempo"
        value={`${interventiInTempo}/${interventiConScadenza}`}
        accent="text-blue-700"
      />
      {percentuale != null && (
        <Stat label="Percentuale" value={`${percentuale}%`} />
      )}
      <Stat label="Totale interventi" value={dati.interventiTotali} />
    </div>
  );
}

function RapportinoCompleteness({ dati }) {
  if (!dati || dati.interventiTotali === 0) {
    return <Note>{dati?.nota || "Nessun intervento nel periodo"}</Note>;
  }
  const { conRapportino, senzaRapportino, interventiTotali, percentuale } =
    dati;
  return (
    <div className="flex gap-4 flex-wrap">
      <Stat
        label="Con rapportino"
        value={`${conRapportino}/${interventiTotali}`}
        accent="text-blue-700"
      />
      {senzaRapportino > 0 && (
        <Stat
          label="Senza rapportino"
          value={senzaRapportino}
          accent="text-red-600"
        />
      )}
      {percentuale != null && (
        <Stat label="Percentuale" value={`${percentuale}%`} />
      )}
    </div>
  );
}

function CompletionRate({ dati }) {
  if (!dati || dati.scadenzeTotali === 0) {
    return <Note>{dati?.nota || "Nessuna scadenza nel periodo"}</Note>;
  }
  const { completate, nonCompletate, scadenzeTotali, percentuale } = dati;
  return (
    <div className="flex gap-4 flex-wrap">
      <Stat
        label="Completate"
        value={`${completate}/${scadenzeTotali}`}
        accent="text-blue-700"
      />
      {nonCompletate > 0 && (
        <Stat
          label="Non completate"
          value={nonCompletate}
          accent="text-red-600"
        />
      )}
      {percentuale != null && (
        <Stat label="Percentuale" value={`${percentuale}%`} />
      )}
    </div>
  );
}

function CorrectiveActions({ dati }) {
  if (!dati) return <Note>Nessun dato</Note>;
  const { azioniTotali, azioniChiuse, azioniAperte } = dati;
  if (azioniTotali === 0) {
    return (
      <span className="text-xs text-green-700">
        Nessuna azione correttiva registrata
      </span>
    );
  }
  return (
    <div className="flex gap-4 flex-wrap">
      <Stat
        label="Totale"
        value={azioniTotali}
        accent={azioniTotali > 4 ? "text-red-600" : "text-amber-700"}
      />
      <Stat label="Chiuse" value={azioniChiuse} accent="text-green-700" />
      <Stat
        label="Aperte"
        value={azioniAperte}
        accent={azioniAperte > 0 ? "text-red-600" : "text-gray-500"}
      />
    </div>
  );
}

function ResponseTime({ dati }) {
  if (!dati || dati.interventiTotali === 0 || dati.interventiAnalizzati === 0) {
    return (
      <Note>{dati?.nota || "Nessun intervento con scadenza nel periodo"}</Note>
    );
  }
  const { giorniMediRitardo, interventiAnalizzati } = dati;
  const accent =
    giorniMediRitardo == null
      ? "text-gray-500"
      : giorniMediRitardo <= 3
        ? "text-green-700"
        : giorniMediRitardo <= 14
          ? "text-amber-700"
          : "text-red-600";
  return (
    <div className="flex gap-4 flex-wrap">
      <Stat
        label="Ritardo medio"
        value={`${giorniMediRitardo} gg`}
        accent={accent}
      />
      <Stat label="Interventi analizzati" value={interventiAnalizzati} />
    </div>
  );
}

function ContractCompliance({ dati }) {
  if (!dati) return <Note>Nessun dato</Note>;
  const {
    contrattiTotali,
    contrattiAttivi,
    contrattiScaduti,
    contrattiCheCopreno,
    nota,
  } = dati;
  if (contrattiTotali === 0) {
    return (
      <Note>{nota || "Nessun contratto registrato (fornitore spot)"}</Note>
    );
  }
  return (
    <div className="flex gap-4 flex-wrap">
      <Stat
        label="Attivi"
        value={`${contrattiAttivi}/${contrattiTotali}`}
        accent="text-green-700"
      />
      {contrattiScaduti > 0 && (
        <Stat label="Scaduti" value={contrattiScaduti} accent="text-red-600" />
      )}
      {contrattiCheCopreno != null && (
        <Stat
          label="Coprono periodo"
          value={`${contrattiCheCopreno}/${contrattiTotali}`}
        />
      )}
    </div>
  );
}

function DocumentCompleteness({ dati }) {
  if (!dati || !dati.dettaglio) return <Note>Nessun dato</Note>;
  const { dettaglio, documentiValidi, documentiObbligatori, bonus } = dati;
  const tipi = Object.entries(dettaglio);
  return (
    <div className="space-y-2">
      <div className="flex gap-4 flex-wrap text-xs">
        <Stat
          label="Obbligatori validi"
          value={`${documentiValidi}/${documentiObbligatori}`}
          accent={
            documentiValidi === documentiObbligatori
              ? "text-green-700"
              : "text-amber-700"
          }
        />
        {bonus > 0 && (
          <Stat label="Bonus" value={`+${bonus}`} accent="text-blue-700" />
        )}
      </div>
      <table className="w-full text-xs border-t border-gray-100">
        <thead>
          <tr className="text-gray-500">
            <th className="text-left py-1 font-medium">Tipo</th>
            <th className="text-center py-1 font-medium w-16">Presenti</th>
            <th className="text-center py-1 font-medium w-20">Score</th>
            <th className="text-left py-1 font-medium">Prossima scadenza</th>
          </tr>
        </thead>
        <tbody>
          {tipi.map(([label, info]) => (
            <tr key={label} className="border-t border-gray-50">
              <td className="py-1 text-gray-700">{label}</td>
              <td className="py-1 text-center">{info.presenti}</td>
              <td className="py-1 text-center">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${scoreBadge(info.score)}`}
                >
                  {info.score}
                </span>
              </td>
              <td className="py-1 text-gray-600">
                {formatDate(info.prossimaScadenza) || (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const RENDERERS = {
  intervention_timeliness: InterventionTimeliness,
  rapportino_completeness: RapportinoCompleteness,
  completion_rate: CompletionRate,
  corrective_actions: CorrectiveActions,
  response_time: ResponseTime,
  contract_compliance: ContractCompliance,
  document_completeness: DocumentCompleteness,
};

export default function KPIDetailRenderer({ formula, dati }) {
  if (!dati) return null;
  if (dati.errore) {
    return <span className="text-xs text-red-600">Errore: {dati.errore}</span>;
  }
  const Renderer = RENDERERS[formula];
  if (!Renderer) {
    return (
      <pre className="text-[11px] text-left bg-gray-50 p-2 rounded max-h-32 overflow-auto">
        {JSON.stringify(dati, null, 2)}
      </pre>
    );
  }
  return <Renderer dati={dati} />;
}
